import type { AccountCleanupRequest } from '@/services/api/account-cleanup';
import type { ParsedCsv } from '@/utils/csv';
import { extractFileName } from '@/utils/string';

export type CleanupFileKind = 'report' | 'outcome' | 'other';

// The listing for the type holds the weekly reports and the PROCESS outcome files written beside them
const FILE_KIND_PREFIXES: ReadonlyArray<[CleanupFileKind, string]> = [
  ['report', 'account-cleanup-report-'],
  ['outcome', 'account-cleanup-process-'],
];

export const cleanupFileKind = (key: string): CleanupFileKind => {
  const fileName = extractFileName(key);
  return FILE_KIND_PREFIXES.find(([, prefix]) => fileName.startsWith(prefix))?.[0] ?? 'other';
};

/** Read by header name, so column order and additions do not matter */
export interface CleanupRow {
  pid: string;
  createdDate: string;
  lastLogin: string;
  status: string;
  loginsCount: string;
  inAuth0: string;
  inSumsub: string;
  sumsubLevel: string;
  sumsubVerdict: string;
  sumsubIdentityAnswer: string;
  sumsubHistory: string;
  authRecordsCount: string;
  fastCustomerDetailsRecordsCount: string;
  piiVaultFileCount: string;
  excludedReason: string;
  isCandidate: boolean;
}

const COLUMN_HEADERS = {
  pid: 'pid',
  createdDate: 'createdDate',
  lastLogin: 'lastLogin',
  status: 'status',
  loginsCount: 'loginsCount',
  inAuth0: 'inAuth0',
  inSumsub: 'inSumsub',
  sumsubLevel: 'sumsubLevel',
  sumsubVerdict: 'sumsubVerdict',
  sumsubIdentityAnswer: 'sumsubIdentityAnswer',
  sumsubHistory: 'sumsubHistory',
  authRecordsCount: 'authRecordsCount',
  fastCustomerDetailsRecordsCount: 'fastCustomerDetailsRecordsCount',
  piiVaultFileCount: 'piiVaultFileCount',
  excludedReason: 'excludedReason',
} as const;

const IS_CANDIDATE_HEADER = 'isCandidate';

export const parseCleanupRows = ({ headers, rows }: ParsedCsv): CleanupRow[] => {
  const indexOf = new Map(headers.map((header, index) => [header.trim(), index]));
  const read = (row: string[], header: string): string => {
    const index = indexOf.get(header);
    return index === undefined ? '' : (row[index] ?? '');
  };

  return rows
    .filter((row) => read(row, COLUMN_HEADERS.pid) !== '')
    .map((row) => ({
      pid: read(row, COLUMN_HEADERS.pid),
      createdDate: read(row, COLUMN_HEADERS.createdDate),
      lastLogin: read(row, COLUMN_HEADERS.lastLogin),
      status: read(row, COLUMN_HEADERS.status),
      loginsCount: read(row, COLUMN_HEADERS.loginsCount),
      inAuth0: read(row, COLUMN_HEADERS.inAuth0),
      inSumsub: read(row, COLUMN_HEADERS.inSumsub),
      sumsubLevel: read(row, COLUMN_HEADERS.sumsubLevel),
      sumsubVerdict: read(row, COLUMN_HEADERS.sumsubVerdict),
      sumsubIdentityAnswer: read(row, COLUMN_HEADERS.sumsubIdentityAnswer),
      sumsubHistory: read(row, COLUMN_HEADERS.sumsubHistory),
      authRecordsCount: read(row, COLUMN_HEADERS.authRecordsCount),
      fastCustomerDetailsRecordsCount: read(row, COLUMN_HEADERS.fastCustomerDetailsRecordsCount),
      piiVaultFileCount: read(row, COLUMN_HEADERS.piiVaultFileCount),
      excludedReason: read(row, COLUMN_HEADERS.excludedReason),
      isCandidate: read(row, IS_CANDIDATE_HEADER) === 'true',
    }));
};

/** The API admits only candidate rows; a FULL-scope report also lists excluded accounts. */
export const isApprovable = (row: CleanupRow): boolean => row.isCandidate;

export const buildCleanupRequest = (reportKey: string, accountHolderIds: string[], auditReference: string, dryRun: boolean): AccountCleanupRequest => ({
  reportKey,
  accountHolderIds: [...new Set(accountHolderIds)].sort(),
  auditReference: auditReference.trim(),
  dryRun,
});
